import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {CollectionData} from "types/Collection";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import CollectionForm from "pages/Collection/Form";
import {useOperator} from "../../../context/OperatorContext";

// Component ----

function MutateCollection() {
  const {termFor} = useOperator();

  const {id} = useParams()
  const {data: collection, isLoading} = useQuery(
    ['collections', {id}],
    () => ngage.collections.get(parseInt(id as string)),
    {enabled: !!id}
  )

  const store = useMutation((data: CollectionData) => ngage.collections.store(data))
  const update = useMutation((data: CollectionData) => ngage.collections.update(parseInt(id as string), data))

  const {notify} = useNotifications()
  const navigate = useNavigate()

  // Handle form submission
  async function handleSubmit(values: CollectionData, {setSubmitting}: FormikHelpers<CollectionData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('collections.all')

    setSubmitting(false)
    navigate('/collections')
  }

  // Handle store mutation
  async function handleStore(values: CollectionData) {
    try {
      await store.mutateAsync(values)
      notify("success", `${termFor('Collection')} Created`, `${values.name} has been created`)
    } catch (error) {
      notify("error", `${termFor('Collection')} Not Created`, `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: CollectionData) {
    try {
      await update.mutateAsync(values)
      notify("success", `${termFor('Collection')} Updated`, `${values.name} has been updated`)
      await invalidate(['collections', {id}])
    } catch (error) {
      notify("error", `${termFor('Collection')} Not Updated`, `${values.name} could not be updated`)
      throw error
    }
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Collections')]: '/collections',
    [`New ${termFor('Collection')}`]: '/collections/new'
  }
  const editBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Collection')]: '/collections',
    [`Edit ${termFor('Collection')}`]: `/collections/${id}/edit`
  }

  return (
    <Page
      title={`${id? 'Edit' : 'New'} ${termFor('Collection')}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <CollectionForm collection={collection} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateCollection;